import { useMemo, useState } from 'react';

import {
  SaleProduct,
  VariantOption,
  calculatePrice,
  findDefaultTerm,
  getProductWithMergedVariants,
  issuesForTerm,
  pricePerIssue,
  selectProductForTerm,
  termPercentage,
} from '@v2/shared/lib/megaSale';

/**
 * The colour of the price, on the tile and in the (i) view alike.
 *
 * It lives beside the pricing the two components share because they have to agree on it:
 * they are the same price in two places, and only the size differs.
 */
export const PRICE_COLOR = 'text-primary-600';

/** The Type option a "New" purchase uses; the gift copy reuses it. */
const TYPE_NEW = 'New';

/**
 * The Type half of a sku key when the product has no Type to choose.
 *
 * A book is sold in one format and one condition: it carries a Term variant holding
 * "Paperback" or "Hardcover" and no Type variant at all, so `jss_skus.extrafieldid2`
 * keeps its default of 0 and the map `findVariantSkuCodesByProductIds` emits is keyed
 * `"<termId>_0"`. Without this the key is `"<termId>_null"`, every book's lookup misses,
 * and the tile renders a permanently disabled Add to Cart for a product that is in stock.
 */
const NO_TYPE = 0;

/**
 * The Term half, likewise, when the product has no Term to choose.
 *
 * A postcard has neither a Term nor a Type to choose, so its one sku is the base sku,
 * keyed `"0_0"`. Without this there was no term to key by at all, and every postcard on
 * the sale rendered a disabled Add to Cart.
 */
const NO_TERM = 0;

export type ProductTileState = {
  /** The group member that actually sells the chosen term — what gets added to the cart. */
  product: SaleProduct;
  /** The tile's product with every group member's variant options merged in. */
  merged: SaleProduct;
  selectedTerm: VariantOption | null;
  selectedTypeId: string | number | null;
  setSelectedTermId: (id: string | number) => void;
  setSelectedTypeId: (id: string | number) => void;
  price: number;
  issues: number | null;
  perIssue: number | null;
  /**
   * The sku the current selection buys, or null when the catalogue has none for it.
   *
   * Null is a real answer and not a bug to swallow: the storefront cart api adds by sku
   * code, so a selection with no sku cannot be bought and the tile has to say so rather
   * than render a button that 400s.
   */
  skuCode: string | null;
  /** The sku for the same term bought as a gift — the New type, whatever is selected. */
  giftSkuCode: string | null;
};

/**
 * Everything a tile computes from the customer's Term and Type choices.
 *
 * Shared by the tile and the detail dialog, which is the whole reason it is a hook: the
 * dialog is the same product with the same selection and has to price it identically. In
 * the legacy component the dialog was a second copy of the tile component rendering
 * itself recursively, and the two drifted.
 */
export default function useProductTile(product: SaleProduct, highestTerm: boolean): ProductTileState {
  const merged = useMemo(
    () => getProductWithMergedVariants(product, product.grouped_products),
    [product]
  );

  const [selectedTermId, setSelectedTermId] = useState<string | number | null>(
    () => findDefaultTerm(merged, highestTerm)?.id ?? null
  );

  const typeVariant = merged.variants.find((variant) => variant.name === 'Type');
  const [selectedTypeId, setSelectedTypeId] = useState<string | number | null>(
    () => typeVariant?.options?.[0]?.id ?? null
  );

  const selectedTerm =
    (merged.restricted_terms || []).find((term) => String(term.id) === String(selectedTermId)) ?? null;

  // The tile shows one product but a group sells the terms across several rows, so
  // everything priced or bought follows the selection to the row that owns it.
  const owner = selectProductForTerm(product, selectedTermId);

  const price = calculatePrice(selectedTerm, owner);
  const issues = issuesForTerm(owner, selectedTerm);

  /*
   * Whether the product has a Term at all — read from its variants rather than from
   * `restricted_terms`, which is what this sale leaves of them. A product whose every
   * term the sale restricts still has terms, none of them for sale here, and stays
   * unbuyable rather than falling through to its base sku.
   */
  const hasTerms =
    (merged.variants.find((variant) => variant.name === 'Term')?.options || []).length > 0;

  const skuFor = (typeId: string | number | null | undefined): string | null => {
    const termId = hasTerms ? selectedTermId : NO_TERM;

    if (termId === null) {
      return null;
    }

    return owner.skus?.[`${termId}_${typeId ?? NO_TYPE}`] ?? null;
  };

  const newTypeId = typeVariant?.options?.find((option) => option.option === TYPE_NEW)?.id;

  return {
    product: owner,
    merged,
    selectedTerm,
    selectedTypeId,
    setSelectedTermId,
    setSelectedTypeId,
    price,
    issues,
    perIssue: pricePerIssue(price, issues),
    skuCode: skuFor(selectedTypeId),
    /*
     * A gift is a New subscription sent to somebody else — there is no Gift sku, which
     * is why the legacy form's "Gift" option carried the New option's id.
     *
     * No New option, no gift, and this is where that rule lives now. It used to fall back
     * to whatever Type was selected, which was harmless while everything on a sale had a
     * Type — and stopped being harmless with books, which have none: the fallback resolved
     * to the book's own sku and the page would have offered a second copy of a paperback
     * as a gift, an option the legacy page never drew because it had no `option.gift-option`
     * to draw it from.
     */
    giftSkuCode: newTypeId === undefined ? null : skuFor(newTypeId),
  };
}

export { termPercentage };
